import api from '@/api';

import {
  UpdateWorkOrderComplementRequest,
  WorkorderActionsRequest,
  WorkOrderComplement,
  CreateWorkOrderComplementRequest,
  WorkorderCancelRequest,
  WorkOrderCloseVerifyResponse,
  WorkOrderCloseVerifyRequest,
  WorkOrderUpdateEstimatedDateRequest,
  WorkOrderComplementInvoiceValuesRequest,
  WorkOrderComplementInvoiceValues,
  WorkOrderComplementIncludeTagRequest,
  WorkOrderComplementChangeStatusRequest,
  WorkOrderComplementSignaturePublic,
  WorkOrderComplementGenerateActivity,
  WorkOrderComplementItemsHitoryRequest,
  WorkOrderComplementItemHistoryResponse,
  CloseAllWorkOrderComplementRequest,
  WorkOrderCloseAllVerifyResponse,
  WorkOrderComplementSyncWithPecaAiParams,
  WorkOrderComplementBindItemsWithOperational,
  WorkOrderComplementCreatePecaAiOrderRequest,
  WorkOrderComplementBindWithPecaAiParams,
  WorkOrderComplementCreatePecaAiOrder,
  WorkOrderComplementSyncWithMyBCSRequest,
  WorkOrderComplementEditScheduleRequest,
} from './WorkOrdersComplements.types';

import { API_BASE_URL } from '..';
import OAuth from '@/core/libs/OAuth/OAuth.class';
import moment from 'moment';

const CONTROLLER_URL = `${API_BASE_URL}/workOrders`;

export async function getWorkOrderComplement(complementId?: number) {
  const result = (await api.get(
    `${CONTROLLER_URL}/complements/${complementId}`,
    null,
  )) as unknown;

  return result as WorkOrderComplement;
}

export async function createWorkOrderComplement(
  obj: CreateWorkOrderComplementRequest,
) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${obj.workOrderId}/complements/${obj.complementId}`,
    obj,
    null,
  )) as unknown;

  return result as { id: number };
}

export async function updateWorkOrderComplement(
  obj: UpdateWorkOrderComplementRequest,
): Promise<WorkOrderComplement> {
  const result = (await api.put(
    `${CONTROLLER_URL}/${obj.workOrderId}/complements/${obj.complementId}`,
    obj,
    null,
  )) as unknown;

  return result as WorkOrderComplement;
}

export async function authorizeWorkOrderComplement(
  data: WorkorderActionsRequest,
) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${data.workorderId}/Complements/${data.complementId}/authorize`,
    { authorizedSignature: data?.authorizedSignature },
    null,
    { headers: { 'X-Time-Zone': moment().format('Z') } },
  )) as unknown;

  return result as void;
}

export async function verifyAuthorizeWorkOrderComplement(
  data: WorkorderActionsRequest,
) {
  const result = (await api.get(
    `${CONTROLLER_URL}/${data.workorderId}/Complements/${data.complementId}/authorize`,
    null,
  )) as unknown;

  return result as void;
}

export async function cancelWorkOrderComplement({
  cancellationReason,
  ...data
}: WorkorderCancelRequest) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${data.workorderId}/Complements/${data.complementId}/cancel`,
    { cancellationReason },
    null,
  )) as unknown;

  return result as void;
}

export async function closeWorkOrderComplement(data: WorkorderActionsRequest) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${data.workorderId}/Complements/${data.complementId}/close`,
    data,
    null,
    { headers: { 'X-Time-Zone': moment().format('Z') } },
  )) as unknown;

  return result as void;
}

export async function closeAllWorkOrderComplement(
  params: CloseAllWorkOrderComplementRequest,
) {
  const { workOrderId, ...obj } = params;

  const result = (await api.post(
    `${CONTROLLER_URL}/${workOrderId}/close-many`,
    obj,
    null,
    { headers: { 'X-Time-Zone': moment().format('Z') } },
  )) as unknown;

  return result as void;
}

export async function reopenWorkOrderComplement(data: WorkorderActionsRequest) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${data.workorderId}/Complements/${data.complementId}/reopen`,
    {},
    null,
  )) as unknown;

  return result as void;
}

export async function closeVerifyWorkOrderComplement(
  data: WorkOrderCloseVerifyRequest,
) {
  const result = (await api.get(
    `${CONTROLLER_URL}/${data.workorderId}/Complements/${data.complementId}/close`,
    null,
    { options: { headers: { 'X-Time-Zone': moment().format('Z') } } },
  )) as unknown;

  return result as WorkOrderCloseVerifyResponse;
}

export async function closeAllVerifyWorkOrderComplement(workOrderId: number) {
  const result = (await api.get(
    `${CONTROLLER_URL}/${workOrderId}/pendencies-close-many`,
    {},
    { options: { headers: { 'X-Time-Zone': moment().format('Z') } } },
  )) as unknown;
  return result as WorkOrderCloseAllVerifyResponse;
}

export async function updateItemsWorkOrderComplement(
  data: WorkOrderCloseVerifyRequest,
) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${data.workorderId}/Complements/${data.complementId}/updateitems`,
    {},
  )) as unknown;

  return result as void;
}

export async function updateEstimatedDateWorkOrderComplement({
  workorderId,
  ...data
}: WorkOrderUpdateEstimatedDateRequest) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${workorderId}/Complements/${data.complementId}/estimateddate`,
    data,
  )) as unknown;

  return result as void;
}

export async function workorderComplementInvoiceValues({
  workorderId,
  ...data
}: WorkOrderComplementInvoiceValuesRequest) {
  const result = (await api.get(
    `${CONTROLLER_URL}/${workorderId}/Complements/invoice-values`,
    data,
  )) as unknown;

  return result as WorkOrderComplementInvoiceValues;
}

export async function workorderComplementIncludeTag({
  workorderId,
  complementId,
  ...data
}: WorkOrderComplementIncludeTagRequest) {
  const result = (await api.patch(
    `${CONTROLLER_URL}/${workorderId}/Complements/${complementId}/tag`,
    data,
    null,
  )) as unknown;

  return result;
}

export async function workorderComplementChangeStatus({
  workorderId,
  complementId,
  ...data
}: WorkOrderComplementChangeStatusRequest) {
  const result = (await api.patch(
    `${CONTROLLER_URL}/${workorderId}/Complements/${complementId}/status`,
    data,
    null,
  )) as unknown;

  return result;
}

export async function workorderComplementSignaturePublic({
  complementGuid,
  ...data
}: WorkOrderComplementSignaturePublic) {
  const result = (await api.post(
    `workorder/v1/Complements/Signature/public/${complementGuid}`,
    data,
  )) as unknown;

  return result as void;
}

export async function workorderComplementGenerateActivity(
  params: WorkOrderComplementGenerateActivity,
) {
  const result = (await api.post(
    `workorder/v1/workorders/${params.workOrderId}/Complements/${params.complementId}/generate-activity`,
    {},
  )) as unknown;

  return result as void;
}

export async function workOrderComplementItemsHistory({
  workOrderId,
  complementId,
  ...params
}: WorkOrderComplementItemsHitoryRequest) {
  const result = (await api.get(
    `${CONTROLLER_URL}/${workOrderId}/Complements/${complementId}/items-history`,
    params,
  )) as unknown;
  return result as WorkOrderComplementItemHistoryResponse;
}

export async function workorderComplementBindWithPecaAi(
  data: WorkOrderComplementBindWithPecaAiParams,
) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${data.workOrderId}/Complements/${data.complementId}/bind-with-peca-ai`,
    data,
  )) as unknown;

  return result as void;
}

export async function workorderComplementSyncWithPecaAi(
  params: WorkOrderComplementSyncWithPecaAiParams,
) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${params.workOrderId}/Complements/${params.complementId}/sync-with-peca-ai`,
    {},
  )) as unknown;

  return result as void;
}

type CredentialTokens = { access_token?: string };

export async function workorderComplementSyncWithWom(
  params: WorkOrderComplementSyncWithPecaAiParams,
) {
  const womTokenJson = OAuth.getCredentialTokens<CredentialTokens>('WOM');
  const womToken = womTokenJson?.access_token;

  const result = (await api.post(
    `${CONTROLLER_URL}/${params.workOrderId}/Complements/${params.complementId}/sync-with-wom`,
    {},
    null,
    { headers: { womToken } },
  )) as unknown;

  return result as void;
}

export async function workorderComplementBindItemsWithOperational(
  params: WorkOrderComplementBindItemsWithOperational,
) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${params.workOrderId}/Complements/${params.complementId}/bind-items-with-operational`,
    {},
    null,
    { headers: { womToken: null } },
  )) as unknown;

  return result as void;
}

export async function workorderComplementCreatePecaAiOrder(
  params: WorkOrderComplementCreatePecaAiOrderRequest,
) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${params.workOrderId}/Complements/${params.complementId}/create-peca-ai-order`,
    {},
  )) as unknown;

  return result as void;
}

export async function workOrderComplementCreatePecaAiOrder(
  params: WorkOrderComplementCreatePecaAiOrder,
) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${params.workOrderId}/Complements/${params.complementId}/create-peca-ai-order`,
    {},
  )) as unknown;

  return result as void;
}

export async function workOrderComplementSyncWithMyBCS({
  workOrderId,
  complementId,
}: WorkOrderComplementSyncWithMyBCSRequest) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${workOrderId}/Complements/${complementId}/sync-with-my-bcs`,
    {},
  )) as unknown;

  return result;
}

export async function workOrderComplementEditSchedule({
  workOrderId,
  complementId,
  ...data
}: WorkOrderComplementEditScheduleRequest) {
  return await api.put(
    `${CONTROLLER_URL}/${workOrderId}/Complements/${complementId}/edit-schedule`,
    data,
    null,
  );
}
